import * as React from "react"
import { AnimatePresence, motion } from "framer-motion"
import { C, S, SITE_BG, T, SPRING } from "./tokens"
import { Press, Icon, Skeleton } from "./prims"
import {
    getSession, signInWithPassword, signUp as sbSignUp, resetPasswordForEmail as sbResetPassword,
    updateUserPassword, signOut as sbSignOut, onAuthStateChange, captureSessionFromUrlHash,
    getRememberDevice, SupabaseSession,
} from "./lib/supabase"

export interface SessionUser {
    id: string
    email: string
    name: string | null
}

export interface UseSession {
    status: "loading" | "authenticated" | "unauthenticated"
    user: SessionUser | null
    /** True right after a recovery-link hash was captured — routes straight
     *  to the "set new password" screen instead of the normal login form. */
    recovering: boolean
    getAccessToken: () => Promise<string | null>
    signIn: (email: string, password: string, remember?: boolean) => Promise<{ error: string | null }>
    signUp: (email: string, password: string, remember?: boolean) => Promise<{ error: string | null; needsConfirmation: boolean }>
    resetPassword: (email: string) => Promise<{ error: string | null }>
    completeRecovery: (newPassword: string) => Promise<{ error: string | null }>
    signOut: () => Promise<void>
}

function toUser(s: SupabaseSession | null): SessionUser | null {
    if (!s?.user) return null
    const meta = s.user.user_metadata ?? {}
    return { id: s.user.id, email: s.user.email, name: meta.full_name ?? meta.name ?? null }
}

/** The one allow-listed destination every confirmation/recovery link should
 *  point back at — this app's own current origin, whatever that happens to
 *  be (a Pages preview URL or the production domain). Supabase silently
 *  drops the redirect and falls back to the bare site URL if this exact
 *  value isn't in Authentication → URL Configuration's allow list, so
 *  signup/reset only complete their email round-trip once that's added. */
const EMAIL_REDIRECT_URL = typeof window !== "undefined" ? window.location.origin + "/" : undefined

export function useSession(): UseSession {
    const [status, setStatus] = React.useState<UseSession["status"]>("loading")
    const [user, setUser] = React.useState<SessionUser | null>(null)
    const [recovering, setRecovering] = React.useState(false)

    React.useEffect(() => {
        let live = true
        const hashType = captureSessionFromUrlHash()
        getSession().then((s) => {
            if (!live) return
            setUser(toUser(s))
            setStatus(s ? "authenticated" : "unauthenticated")
            if (s && hashType === "recovery") setRecovering(true)
        })
        const unsubscribe = onAuthStateChange((s) => {
            setUser(toUser(s))
            setStatus(s ? "authenticated" : "unauthenticated")
        })
        return () => {
            live = false
            unsubscribe()
        }
    }, [])

    const getAccessToken = React.useCallback(async () => {
        const s = await getSession()
        if (!s) {
            setUser(null)
            setStatus("unauthenticated")
            return null
        }
        return s.access_token
    }, [])

    const signIn = React.useCallback(async (email: string, password: string, remember: boolean = true) => {
        const { session, error } = await signInWithPassword(email, password, remember)
        if (error || !session) return { error: error ?? "Erè. Eseye ankò." }
        setUser(toUser(session))
        setStatus("authenticated")
        return { error: null }
    }, [])

    const signUp = React.useCallback(async (email: string, password: string, remember: boolean = true) => {
        const { error } = await sbSignUp(email, password, EMAIL_REDIRECT_URL)
        if (error) return { error, needsConfirmation: false }
        // Project has email confirmation off → signUp already saved a live
        // session (see src/lib/supabase.tsx) — pick it straight up rather
        // than making the user log in again right after creating the account.
        const s = await getSession()
        if (s) {
            setUser(toUser(s))
            setStatus("authenticated")
            return { error: null, needsConfirmation: false }
        }
        return { error: null, needsConfirmation: true }
    }, [])

    const resetPassword = React.useCallback(async (email: string) => {
        return sbResetPassword(email, EMAIL_REDIRECT_URL)
    }, [])

    const completeRecovery = React.useCallback(async (newPassword: string) => {
        const { error } = await updateUserPassword(newPassword)
        if (error) return { error }
        setRecovering(false)
        const s = await getSession()
        setUser(toUser(s))
        setStatus(s ? "authenticated" : "unauthenticated")
        return { error: null }
    }, [])

    const signOut = React.useCallback(async () => {
        await sbSignOut()
        setUser(null)
        setStatus("unauthenticated")
        setRecovering(false)
    }, [])

    return { status, user, recovering, getAccessToken, signIn, signUp, resetPassword, completeRecovery, signOut }
}

// ─── Validation helpers — ported from the real Auth.tsx ────────────────────
const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

function getPasswordStrength(pwd: string) {
    if (!pwd) return { score: 0, label: "", color: "transparent" }
    let score = 0
    if (pwd.length >= 6) score++
    if (pwd.length >= 10) score++
    if (/[A-Z]/.test(pwd)) score++
    if (/[0-9]/.test(pwd)) score++
    if (/[^A-Za-z0-9]/.test(pwd)) score++
    if (score <= 1) return { score, label: "Fèb", color: C.danger }
    if (score <= 3) return { score, label: "Mwayen", color: C.warn }
    return { score, label: "Solid", color: C.ok }
}

type Mode = "login" | "signup" | "forgot" | "reset"

/** Full-viewport auth gate — login, create-account, forgot-password and
 *  set-new-password, one screen with smooth cross-fades between modes
 *  rather than four separate pages. Copy lifted from the real Auth.tsx;
 *  every visual is this demo's own design system (.field/.cta/.g-card),
 *  not that file's inline Framer styles. */
export function LoginScreen({ session }: { session: UseSession }) {
    const initialMode: Mode = session.recovering ? "reset" : "login"
    const [mode, setMode] = React.useState<Mode>(initialMode)
    React.useEffect(() => { if (session.recovering) setMode("reset") }, [session.recovering])

    const [email, setEmail] = React.useState("")
    const [password, setPassword] = React.useState("")
    const [confirm, setConfirm] = React.useState("")
    const [showPassword, setShowPassword] = React.useState(false)
    const [showConfirm, setShowConfirm] = React.useState(false)
    const [remember, setRemember] = React.useState(() => getRememberDevice())
    const [loading, setLoading] = React.useState(false)
    const [error, setError] = React.useState("")
    const [success, setSuccess] = React.useState("")
    const [emailTouched, setEmailTouched] = React.useState(false)
    const [confirmTouched, setConfirmTouched] = React.useState(false)
    const emailRef = React.useRef<HTMLInputElement>(null)
    const lastSubmit = React.useRef(0)

    React.useEffect(() => {
        const t = setTimeout(() => emailRef.current?.focus(), 100)
        return () => clearTimeout(t)
    }, [mode])

    const switchMode = (next: Mode) => {
        setMode(next)
        setError(""); setSuccess("")
        setPassword(""); setConfirm("")
        setEmailTouched(false); setConfirmTouched(false)
    }

    const strength = getPasswordStrength(password)
    const emailError = emailTouched && email && !isValidEmail(email) ? "Imèl sa a pa valid." : null
    const confirmError = confirmTouched && confirm && password !== confirm ? "Modpas yo pa koresponn." : null

    const submit = async () => {
        const now = Date.now()
        if (now - lastSubmit.current < 500) return
        lastSubmit.current = now
        setError(""); setSuccess("")

        if (mode === "reset") {
            if (!password || !confirm) return setError("Tanpri ranpli tout chanm yo.")
            if (password !== confirm) return setError("Modpas yo pa koresponn.")
            if (password.length < 6) return setError("Modpas dwe gen omwen 6 karaktè.")
        } else {
            if (!email || (mode !== "forgot" && !password)) return setError("Tanpri ranpli tout chanm yo.")
            if (!isValidEmail(email)) return setError("Imèl sa a pa valid.")
            if (mode === "signup" && password !== confirm) return setError("Modpas yo pa koresponn.")
            if (mode !== "forgot" && password.length < 6) return setError("Modpas dwe gen omwen 6 karaktè.")
        }

        setLoading(true)
        if (mode === "login") {
            const { error } = await session.signIn(email.trim(), password, remember)
            if (error) setError(error)
        } else if (mode === "signup") {
            const { error, needsConfirmation } = await session.signUp(email.trim(), password, remember)
            if (error) setError(error)
            else if (needsConfirmation) {
                setSuccess("Kont kreye! Tcheke imèl ou pou konfime kont ou.")
                switchMode("login")
            }
            // else: signUp already logged the user straight in — the
            // status flip to "authenticated" unmounts this screen on its own.
        } else if (mode === "forgot") {
            const { error } = await session.resetPassword(email.trim())
            if (error) setError(error)
            else setSuccess("Imèl voye! Tcheke bwat imèl ou.")
        } else if (mode === "reset") {
            const { error } = await session.completeRecovery(password)
            if (error) setError(error)
            // else: completeRecovery flips status to authenticated itself.
        }
        setLoading(false)
    }

    const handleKeyDown = (e: React.KeyboardEvent) => { if (e.key === "Enter") submit() }

    const title = mode === "login" ? "BYENVINI!" : mode === "signup" ? "KREYE KONT OU"
        : mode === "reset" ? "NOUVO MODPAS" : "MODPAS BLIYE?"
    const buttonLabel = loading ? "..." : mode === "login" ? "KONEKTE" : mode === "signup" ? "ENSKRI"
        : mode === "reset" ? "CHANJE MODPAS" : "VOYE IMÈL"
    const canSubmit = !loading && (mode === "reset" ? password && confirm : email && (mode === "forgot" || password))

    return (
        <div style={{
            position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center",
            justifyContent: "center", padding: S[5], background: SITE_BG,
        }}>
            <motion.div className="g g-card" layout transition={SPRING}
                style={{ width: "100%", maxWidth: 380, padding: S[6], display: "flex", flexDirection: "column", gap: S[5] }}>
                <div style={{ display: "flex", flexDirection: "column", gap: S[2], alignItems: "center", textAlign: "center" }}>
                    <div style={{ width: 48, height: 48, borderRadius: 999, background: C.teal, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Icon name="wallet" size={24} color="#fff" stroke={2} />
                    </div>
                    <AnimatePresence mode="wait" initial={false}>
                        <motion.h1 key={title}
                            initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                            transition={T.quick}
                            style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.text }}>
                            {title}
                        </motion.h1>
                    </AnimatePresence>
                    {(mode === "forgot" || mode === "reset") && (
                        <p className="hint" style={{ textAlign: "center" }}>
                            {mode === "forgot"
                                ? "Antre imèl ou epi nou ap voye yon lyen pou reyinisyalize modpas ou."
                                : "Antre yon nouvo modpas pou kont ou."}
                        </p>
                    )}
                </div>

                <div aria-live="polite" aria-atomic="true">
                    {error && (
                        <div className="vfy-err" role="alert" style={{ justifyContent: "center" }}>
                            <Icon name="alert" size={15} />{error}
                        </div>
                    )}
                    {success && !error && (
                        <div className="vfy-err" role="status" style={{ justifyContent: "center", color: C.ok }}>
                            <Icon name="check" size={15} color={C.ok} />{success}
                        </div>
                    )}
                </div>

                <AnimatePresence mode="wait" initial={false}>
                    <motion.div key={mode}
                        initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }}
                        transition={T.quick}
                        style={{ display: "flex", flexDirection: "column", gap: S[3] }}>

                        {mode !== "reset" && (
                            <label className="field g g-chip">
                                <Icon name="user" size={18} color={C.textMute} />
                                <input ref={emailRef} type="email" value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    onBlur={() => setEmailTouched(true)} onKeyDown={handleKeyDown}
                                    placeholder="Imèl" aria-label="Imèl" autoComplete="email" />
                            </label>
                        )}
                        {emailError && <div className="vfy-err">{emailError}</div>}

                        {mode !== "forgot" && (
                            <label className="field g g-chip">
                                <Icon name="shield" size={18} color={C.textMute} />
                                <input type={showPassword ? "text" : "password"} value={password}
                                    onChange={(e) => setPassword(e.target.value)} onKeyDown={handleKeyDown}
                                    placeholder="Modpas" aria-label="Modpas"
                                    autoComplete={mode === "login" ? "current-password" : "new-password"} />
                                <Press as="div" scale={0.9} onClick={() => setShowPassword((v) => !v)}
                                       aria-label={showPassword ? "Kache modpas" : "Montre modpas"}>
                                    <Icon name={showPassword ? "eyeOff" : "eye"} size={18} color={C.textMute} />
                                </Press>
                            </label>
                        )}

                        {(mode === "signup" || mode === "reset") && password && (
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                <div style={{ display: "flex", gap: 4 }}>
                                    {[0, 1, 2].map((i) => (
                                        <div key={i} style={{
                                            height: 3, flex: 1, borderRadius: 999,
                                            background: strength.score >= (i + 1) * (5 / 3) ? strength.color : "rgba(11,46,44,.1)",
                                            transition: "background .2s",
                                        }} />
                                    ))}
                                </div>
                                {strength.label && <span className="hint" style={{ color: strength.color }}>{strength.label}</span>}
                            </div>
                        )}

                        {(mode === "signup" || mode === "reset") && (
                            <label className="field g g-chip">
                                <Icon name="shield" size={18} color={C.textMute} />
                                <input type={showConfirm ? "text" : "password"} value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    onBlur={() => setConfirmTouched(true)} onKeyDown={handleKeyDown}
                                    placeholder="Konfime modpas" aria-label="Konfime modpas" autoComplete="new-password" />
                                <Press as="div" scale={0.9} onClick={() => setShowConfirm((v) => !v)}
                                       aria-label={showConfirm ? "Kache modpas" : "Montre modpas"}>
                                    <Icon name={showConfirm ? "eyeOff" : "eye"} size={18} color={C.textMute} />
                                </Press>
                            </label>
                        )}
                        {confirmError && <div className="vfy-err">{confirmError}</div>}

                        {(mode === "login" || mode === "signup") && (
                            <Press as="div" role="button" tabIndex={0} scale={0.99}
                                   onClick={() => setRemember((v) => !v)}
                                   style={{ display: "flex", alignItems: "center", gap: S[2], cursor: "pointer" }}>
                                <span style={{
                                    width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                                    border: `1.5px solid ${remember ? C.teal : C.hairline}`,
                                    background: remember ? C.teal : "transparent",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                }}>
                                    {remember && <Icon name="check" size={12} color="#fff" stroke={3} />}
                                </span>
                                <span className="hint">Sonje aparèy sa a</span>
                            </Press>
                        )}
                    </motion.div>
                </AnimatePresence>

                <Press className="cta" disabled={!canSubmit} onClick={submit}>
                    {loading ? <><span className="spin" /> N ap travay…</> : buttonLabel}
                </Press>

                <div style={{ display: "flex", flexDirection: "column", gap: S[2], alignItems: "center" }}>
                    {mode === "login" && (
                        <>
                            <Press as="div" role="button" tabIndex={0} scale={0.98} onClick={() => switchMode("forgot")}
                                   style={{ color: C.teal, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                                Modpas bliye?
                            </Press>
                            <span className="hint">
                                Ou pa gen kont?{" "}
                                <Press as="span" role="button" tabIndex={0} onClick={() => switchMode("signup")}
                                       style={{ color: C.teal, fontWeight: 700, cursor: "pointer" }}>
                                    Enskri
                                </Press>
                            </span>
                        </>
                    )}
                    {mode === "signup" && (
                        <span className="hint">
                            Ou gen yon kont deja?{" "}
                            <Press as="span" role="button" tabIndex={0} onClick={() => switchMode("login")}
                                   style={{ color: C.teal, fontWeight: 700, cursor: "pointer" }}>
                                Konekte
                            </Press>
                        </span>
                    )}
                    {mode === "forgot" && (
                        <Press as="div" role="button" tabIndex={0} scale={0.98} onClick={() => switchMode("login")}
                               style={{ color: C.teal, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                            Retounen nan koneksyon
                        </Press>
                    )}
                </div>
            </motion.div>
        </div>
    )
}

export function SessionLoadingScreen() {
    return (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", background: SITE_BG }}>
            <Skeleton h={44} w={180} r={999} />
        </div>
    )
}